import {
  EWithdrawalStatus,
  type IAdminWithdrawalDetail,
  WITHDRAWAL_TEXT_MAX_LENGTH,
} from '@mahawi/eshop-common/src/types';
import { Button, Form, Input, InputNumber, Select, Space } from 'antd';
import React, { useEffect } from 'react';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { withdrawalSetStatus } from '../../reducers/withdrawal/reducer';
import { SPACING } from '../../theme/tokens';
import { WITHDRAWAL_STATUS_LABEL } from './labels';

interface IStatusFormValues {
  status: EWithdrawalStatus;
  note?: string;
  refundedAmount?: number | null;
}

interface IStatusFormProps {
  detail: IAdminWithdrawalDetail;
  inProcess: boolean;
}

function StatusForm({
  detail,
  inProcess,
}: IStatusFormProps): React.ReactElement {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<IStatusFormValues>();
  const selectedStatus: EWithdrawalStatus | undefined = Form.useWatch(
    'status',
    form,
  );

  useEffect((): void => {
    form.setFieldsValue({
      status: detail.status,
      note: '',
      refundedAmount: detail.refundedAmount,
    });
  }, [detail, form]);

  const statusOptions = Object.values(EWithdrawalStatus).map(
    (status: EWithdrawalStatus) => ({
      value: status,
      label: WITHDRAWAL_STATUS_LABEL[status],
      disabled:
        status === EWithdrawalStatus.ORDER_CANCELLED ||
        (status === EWithdrawalStatus.MONEY_REFUNDED && !detail.order),
    }),
  );

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={(values: IStatusFormValues): void => {
        dispatch(
          withdrawalSetStatus({
            uuid: detail.uuid,
            status: values.status,
            note: values.note?.trim() ? values.note.trim() : null,
            refundedAmount:
              values.status === EWithdrawalStatus.MONEY_REFUNDED
                ? (values.refundedAmount ?? null)
                : null,
          }),
        );
      }}
    >
      <Space size={SPACING.lg} align="start" wrap>
        <Form.Item
          name="status"
          label="Status"
          rules={[{ required: true }]}
          style={{ minWidth: '14rem' }}
        >
          <Select options={statusOptions} disabled={inProcess} />
        </Form.Item>
        {selectedStatus === EWithdrawalStatus.MONEY_REFUNDED && (
          <Form.Item
            name="refundedAmount"
            label="Refunded amount"
            rules={[
              { required: true, message: 'Enter the refunded amount.' },
              {
                type: 'number',
                min: 0.01,
                max: detail.orderTotal ?? undefined,
                message: `Between 0.01 and ${detail.orderTotal ?? 0}.`,
              },
            ]}
          >
            <InputNumber
              min={0}
              max={detail.orderTotal ?? undefined}
              step={0.01}
              suffix={detail.currencyIsoCode ?? ''}
              disabled={inProcess}
            />
          </Form.Item>
        )}
      </Space>
      <Form.Item
        name="note"
        label={
          selectedStatus === EWithdrawalStatus.REJECTED
            ? 'Reason for the rejection'
            : 'Note (optional)'
        }
        rules={[
          {
            required: selectedStatus === EWithdrawalStatus.REJECTED,
            whitespace: true,
            message: 'A rejection needs a reason.',
          },
          { max: WITHDRAWAL_TEXT_MAX_LENGTH },
        ]}
      >
        <Input.TextArea rows={3} disabled={inProcess} />
      </Form.Item>
      <Button type="primary" htmlType="submit" disabled={inProcess}>
        Save status
      </Button>
    </Form>
  );
}

export default StatusForm;
